import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Trophy, Medal, Star, Award, GraduationCap, Palette,
  Music, BookOpen, Cpu, Users, ChevronRight, Filter
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ShareToWhatsApp } from "../components/common/ShareToWhatsApp";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const categories = [
  { id: "all", label: "All", icon: Star },
  { id: "academic", label: "Academic", icon: GraduationCap },
  { id: "sports", label: "Sports", icon: Medal },
  { id: "arts", label: "Arts & Culture", icon: Palette },
  { id: "leadership", label: "Leadership", icon: Users },
  { id: "stem", label: "STEM", icon: Cpu },
];

const achievements = [
  { name: "Akua Mensah", title: "Overall Best Student 2025", category: "academic", grade: "JHS 3", description: "Achieved the highest aggregate score in the 2025 BECE mock examinations with straight 1s across all subjects.", image: "https://images.unsplash.com/photo-1589156280159-27698a70f29e?w=300&q=70", badge: "gold", date: "Dec 2025" },
  { name: "Kwame Asante", title: "National Science Quiz Champion", category: "stem", grade: "JHS 2", description: "Led the YCPS team to victory at the 2025 National Science and Maths Quiz regional finals.", image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=300&q=70", badge: "gold", date: "Nov 2025" },
  { name: "Ama Boateng", title: "Inter-Schools Athletics Champion", category: "sports", grade: "Class 6", description: "Won gold medals in 100m, 200m, and long jump at the Western Region Inter-Schools Athletics Championship.", image: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=300&q=70", badge: "gold", date: "Oct 2025" },
  { name: "Yaw Darko", title: "Best Creative Writer", category: "arts", grade: "JHS 1", description: "Published winning entry in the National Essay Writing Competition on 'The Future of Ghana'.", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=70", badge: "silver", date: "Sep 2025" },
  { name: "Efua Owusu", title: "Head Girl 2025/2026", category: "leadership", grade: "JHS 3", description: "Elected by peers and staff for outstanding leadership, academic excellence, and community service.", image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&q=70", badge: "gold", date: "Sep 2025" },
  { name: "Kofi Adu", title: "Robotics Competition Winner", category: "stem", grade: "JHS 2", description: "Built an autonomous line-following robot that won first place at the Takoradi Tech Fair.", image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&q=70", badge: "gold", date: "Aug 2025" },
  { name: "Adwoa Poku", title: "Best in Mathematics", category: "academic", grade: "Class 5", description: "Scored 100% in three consecutive term exams and represented the school at the Maths Olympiad.", image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&q=70", badge: "silver", date: "Jul 2025" },
  { name: "Nana Yeboah", title: "Football Team Captain - Regional Champions", category: "sports", grade: "JHS 3", description: "Captained the YCPS football team to win the Western Region Inter-Schools Football Tournament.", image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=300&q=70", badge: "gold", date: "Jul 2025" },
  { name: "Abena Serwaa", title: "Cultural Dance Troupe Lead", category: "arts", grade: "Class 6", description: "Led the school's traditional dance group to win the National Cultural Festival competition in Kumasi.", image: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=300&q=70", badge: "silver", date: "Jun 2025" },
  { name: "YCPS Coding Club", title: "Best School Coding Team", category: "stem", grade: "Mixed", description: "Won the Ghana Schools Coding Challenge, building an app that helps farmers track crop prices.", image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=300&q=70", badge: "gold", date: "May 2025" },
  { name: "Emmanuel Tetteh", title: "Perfect Attendance Award", category: "leadership", grade: "Class 4", description: "Maintained 100% attendance for three consecutive academic years — an outstanding record of dedication.", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=70", badge: "silver", date: "Apr 2025" },
  { name: "Priscilla Adjei", title: "Spelling Bee Champion", category: "academic", grade: "Class 3", description: "Won the YCPS Annual Spelling Bee competition, correctly spelling over 150 advanced words.", image: "https://images.unsplash.com/photo-1589156280159-27698a70f29e?w=300&q=70", badge: "silver", date: "Mar 2025" },
];

const badgeColors = {
  gold: "from-yellow-400 to-amber-500",
  silver: "from-gray-300 to-slate-400",
  bronze: "from-orange-400 to-amber-600",
};

const AchievementWallPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");

  const filtered = activeCategory === "all"
    ? achievements
    : achievements.filter(a => a.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="achievement-wall-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-10">
        <div className="max-w-6xl mx-auto px-4">
          <button onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`}
            data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Trophy size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Student Achievement Wall</h1>
              <p className="text-white/70 text-sm mt-1">Celebrating excellence at Young Christian Preparatory School / JHS</p>
            </div>
          </div>

          {/* Stats bar */}
          <div className="flex flex-wrap gap-6 mt-6">
            {[
              { label: "Achievers", value: "120+", icon: Star },
              { label: "Awards Won", value: "85", icon: Trophy },
              { label: "Competitions", value: "34", icon: Medal },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl">
                <s.icon size={16} className="text-[#FF6B00]" />
                <span className="font-bold text-lg">{s.value}</span>
                <span className="text-white/60 text-sm">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Category Filters */}
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${
                activeCategory === cat.id
                  ? "bg-[#002147] text-white"
                  : "bg-white/80 text-[#002147] hover:bg-white"
              } ${btn3D}`}
              data-testid={`filter-${cat.id}`}>
              <cat.icon size={14} /> {cat.label}
            </button>
          ))}
        </div>

        {/* Achievement Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((a, i) => (
            <Card key={i} className="overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group" data-testid={`achievement-${i}`}>
              <div className="relative h-44 overflow-hidden">
                <img src={a.image} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className={`absolute top-3 right-3 w-10 h-10 bg-gradient-to-br ${badgeColors[a.badge]} rounded-full flex items-center justify-center shadow-lg`}>
                  {a.badge === "gold" ? <Trophy size={18} className="text-white" /> : <Medal size={18} className="text-white" />}
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="font-bold text-white text-lg leading-tight">{a.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className="bg-[#FF6B00] text-white text-xs">{a.grade}</Badge>
                    <span className="text-white/60 text-xs">{a.date}</span>
                  </div>
                </div>
              </div>
              <CardContent className="p-4">
                <h4 className="font-semibold text-[#002147] flex items-center gap-1.5 mb-2">
                  <Award size={14} className="text-[#FF6B00]" /> {a.title}
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">{a.description}</p>
                <div className="mt-3 flex items-center justify-between">
                  <Badge variant="outline" className="text-xs capitalize">{a.category}</Badge>
                  <ShareToWhatsApp text={`${a.name} - ${a.title} at YCPS! ${a.description}`} className="text-xs px-3 py-1.5" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Trophy size={48} className="mx-auto text-[#002147]/30 mb-3" />
            <p className="text-[#002147]/60 font-medium">No achievements found in this category</p>
          </div>
        )}

        <div className="text-center mt-6">
          <button onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-5 py-3 bg-white text-[#002147] font-semibold rounded-xl border-2 border-[#002147]/20 ${btn3D} cursor-pointer select-none`}
            data-testid="back-btn-bottom">
            <ArrowLeft size={18} /> Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default AchievementWallPage;
